import { useEffect, useMemo, useRef, useState } from "react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import LayerItem from "./layer-item";
import {
  updateShapeConsumerLocks,
  updateShapeLayerName,
} from "../../../lib/shapes";
import {
  handleManualObjectSelection,
  handleManualSelectAllObjects,
} from "../../../lib/canvas";
import React from "react";
import { GripVertical } from "lucide-react";
import { useResizableSidebar } from "../../../hooks/useResizableSidebar";
import { GroupIcon } from "@radix-ui/react-icons";

// Function to create a dependency array based on shape length and layer names
// optimize re-rendering by only detecting shape important changes for sidebar, layer name and length.
// otherwise it will re-render on every shape change, which is not necessary.
const useShapesDependency = (allShapes: any) => {
  return useMemo(() => {
    // Helper function to extract layer names with hierarchy,
    // we need evert parent name and child name, so when any layer name change, we can update the sidebar.
    const extractLayerNames = (shapes: any, parentName = "") => {
      return shapes.flatMap((shape: any) => {
        const fullName = parentName
          ? `${parentName} > ${shape.layerName}`
          : shape.layerName;

        if (shape.type === "group" && shape._objects) {
          // If it's a group, include its name and recursively process its children
          return [fullName, ...extractLayerNames(shape._objects, fullName)];
        }

        // For regular shapes, include their name with the parent's prefix
        return fullName;
      });
    };

    // Extract all hierarchical layer names
    const layerNames = extractLayerNames(allShapes).join(",");

    const objectIds = allShapes.map((shape: any) => shape.objectId).join(",");

    return {
      length: allShapes.length, // Track the number of top-level shapes/groups
      layerNames, // Track all layer names with hierarchy
      objectIds, // track all object ids, on the top for the layering control.
    };
  }, [allShapes]);
};

const RecursiveLayerItem = ({
  shape,
  depth = 0, // Default depth is 0 for top-level items
  elementAttributes,
  onLayerItemSelect,
  locksMenuOpen,
  handleLocksMenuOpen,
  handleLocksMenuClose,
  selectedCanvasObject,
  setSelectedCanvasObject,
  handleLocksMenuValueChange,
  parentGroupObjectId,
  handleLayerNameChange,
  collapsedGroups,
  toggleGroupCollapse,
  sidebarWidth, // Optional prop for sidebar width
}: any) => {
  const isGroup = shape?.type === "group";
  const isCollapsed = collapsedGroups?.[shape?.objectId];

  return (
    <React.Fragment>
      {/* Render the current LayerItem */}
      <LayerItem
        sidebarWidth={sidebarWidth}
        shape={shape}
        key={shape?.objectId}
        depth={depth} // Pass depth to LayerItem
        selectedElementAttributes={elementAttributes}
        onLayerItemSelect={onLayerItemSelect}
        locksMenuOpen={locksMenuOpen}
        handleLocksMenuOpen={handleLocksMenuOpen}
        handleLocksMenuClose={handleLocksMenuClose}
        selectedCanvasObject={selectedCanvasObject}
        setSelectedCanvasObject={setSelectedCanvasObject}
        handleLocksMenuValueChange={handleLocksMenuValueChange}
        parentGroupObjectId={parentGroupObjectId}
        handleLayerNameChange={handleLayerNameChange}
        isGroup={isGroup}
        isCollapsed={isCollapsed}
        toggleGroupCollapse={toggleGroupCollapse}
      />

      {/* If it's a group, recursively render sub-shapes */}
      {isGroup &&
        !isCollapsed &&
        shape?._objects
          ?.slice()
          .reverse()
          .map((subShape: any) => (
            <RecursiveLayerItem
              key={subShape?.objectId}
              sidebarWidth={sidebarWidth}
              shape={subShape}
              depth={depth + 1} // Increment depth for nested groups
              elementAttributes={elementAttributes}
              onLayerItemSelect={onLayerItemSelect}
              locksMenuOpen={locksMenuOpen}
              handleLocksMenuOpen={handleLocksMenuOpen}
              handleLocksMenuClose={handleLocksMenuClose}
              selectedCanvasObject={selectedCanvasObject}
              setSelectedCanvasObject={setSelectedCanvasObject}
              handleLocksMenuValueChange={handleLocksMenuValueChange}
              parentGroupObjectId={shape?.objectId}
              handleLayerNameChange={handleLayerNameChange}
              collapsedGroups={collapsedGroups}
              toggleGroupCollapse={toggleGroupCollapse}
            />
          ))}
    </React.Fragment>
  );
};

const LeftSidebar = ({
  allShapes,
  setElementAttributes,
  fabricRef,
  isEditingRef,
  activeObjectRef,
  syncShapeInStorage,
  elementAttributes, // the active element attributes in state not ref.
}: any) => {
  // memoize the result of this function so that it doesn't change on every render but only when there are new shapes
  const shapesDependency = useShapesDependency(allShapes);

  const [locksMenuOpen, setLocksMenuOpen] = useState<boolean>(false);

  // will change the current selected canvas object, needs id and consumer locks
  const [selectedCanvasObject, setSelectedCanvasObject] = useState<any>(null);

  // group collapse state, to handle the group open/close state.
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  useEffect(() => {
    console.log(
      "LeftSidebar: allShapes changed, updating collapsedGroups",
      collapsedGroups
    );
  }, [collapsedGroups]);

  // EDIT: for group handling.
  const handleLocksMenuOpen = (canvasObject: any) => {
    setSelectedCanvasObject(canvasObject);
    setLocksMenuOpen(true);
  };

  const handleLocksMenuClose = () => {
    setSelectedCanvasObject(null);
    setLocksMenuOpen(false);
  };

  // group subitem is supported.
  const handleLocksMenuValueChange = (newConsumerLocksObject: any) => {
    // set it in state,
    setSelectedCanvasObject((prev: any) => ({
      ...prev,
      consumerLocks: newConsumerLocksObject,
    }));

    // update the consumer locks in the storage
    // and this will modify the shape in the storage.
    updateShapeConsumerLocks({
      canvas: fabricRef.current,
      objectId: selectedCanvasObject?.objectId,
      newConsumerLocksObj: newConsumerLocksObject,
      syncShapeInStorage,
    });
  };

  // layer name change handler.
  const handleLayerNameChange = (objectId: string, newLayerName: string) => {
    // the objectId, can be in any level, nested group or not.
    // the text input state will be handled inside every layer item internally.
    // only on successful save, we will update the layer name on the canvas object,
    // then inside the storage.

    updateShapeLayerName({
      canvas: fabricRef.current,
      objectId,
      newLayerName,
      syncShapeInStorage,
    });
  };

  // group subitem cannot be selected, to keep fabric internal state immutable.
  const onLayerItemSelect = (objectId: string) => {
    handleManualObjectSelection({
      canvas: fabricRef.current,
      objectId,
    });
  };

  const handleSelectAll = () => {
    handleManualSelectAllObjects({
      canvas: fabricRef.current,
    });
  };

  const { width, handleMouseDown } = useResizableSidebar({
    sidebarKey: "designerLeftSidebarWidth",
    direction: "left",
  });

  const memoizedShapes = useMemo(
    () => (
      <section
        className="z-10 flex flex-col border-t border-borderColor bg-blackLight text-white min-w-[240px] w-[240px] sticky left-0 h-full max-sm:hidden select-none /overflow-y-auto pb-42"
        style={{ width: `${width}px` }}
      >
        <div
          onMouseDown={handleMouseDown}
          className="absolute flex cursor-col-resize z-40 text-primary top-[50%] -right-3 bg-blackLight rounded"
        >
          <GripVertical size={20} />
        </div>

        <div className="p-2 flex border-b border-borderColor items-center justify-between">
          <h3 className="text-xs font-semibold uppercase">Layers</h3>

          <div
            onClick={handleSelectAll}
            title="Select All"
            className="group hover:bg-black p-1 rounded flex items-center justify-center"
          >
            <GroupIcon
              className={
                "text-white group-hover:text-primary group-hover:cursor-pointer"
              }
            />
          </div>
        </div>

        <ScrollArea.Root className="w-full h-full rounded overflow-hidden relative">
          <ScrollArea.Viewport className="w-full h-full rounded">
            <div className="mb-10 pb-2">
              {allShapes
                .slice()
                .reverse()
                .map((shape: any) => (
                  <RecursiveLayerItem
                    key={shape?.objectId}
                    shape={shape}
                    sidebarWidth={width}
                    elementAttributes={elementAttributes}
                    onLayerItemSelect={onLayerItemSelect}
                    locksMenuOpen={locksMenuOpen}
                    handleLocksMenuOpen={handleLocksMenuOpen}
                    handleLocksMenuClose={handleLocksMenuClose}
                    selectedCanvasObject={selectedCanvasObject}
                    setSelectedCanvasObject={setSelectedCanvasObject}
                    handleLocksMenuValueChange={handleLocksMenuValueChange}
                    handleLayerNameChange={handleLayerNameChange}
                    collapsedGroups={collapsedGroups}
                    toggleGroupCollapse={toggleGroupCollapse}
                  />
                ))}
            </div>
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
            orientation="vertical"
          >
            <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[4px] before:min-h-[44px]" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      </section>
    ),

    // re-render only when the shapes change. for elements, only rerender when the objectId change,
    // means the active element has changed.
    [
      width,
      shapesDependency.length, // Re-render only if shape length changes, added or removed objects.
      shapesDependency.layerNames, // Re-render only if layer names change, name change of any layer.
      shapesDependency.objectIds, // Re-render only if object ids change, layering control.
      locksMenuOpen,
      selectedCanvasObject,
      elementAttributes?.objectId,
      elementAttributes?.objectIds, // when active selection change, 3 objs to one.
      elementAttributes?.type,
      elementAttributes?.isCurvedText,
      collapsedGroups, // Re-render if collapsed groups change.
    ]
  );

  return memoizedShapes;
};

export default LeftSidebar;
